<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="95%"
    top="5vh"
    :close-on-click-modal="false"
    class="batch-feed-dialog"
    @close="handleClose"
  >
    <div class="batch-feed-dialog-content">
      <!-- 模式切换 -->
      <div class="mode-radio-group">
        <el-radio-group
          v-model="generateMode"
          size="small"
          @change="onModeChange"
        >
          <el-radio-button label="manual">✏️ 手动配置</el-radio-button>
          <el-radio-button label="target">🎯 目标导向</el-radio-button>
        </el-radio-group>
      </div>

      <!-- ===== 手动模式 ===== -->
      <template v-if="generateMode === 'manual'">
        <div class="config-section">
          <div class="config-header" @click="showConfig = !showConfig">
            <span class="title">
              <i
                :class="
                  showConfig ? 'el-icon-arrow-down' : 'el-icon-arrow-right'
                "
              ></i>
              配料参数配置
            </span>
            <el-button type="text" size="small" @click.stop="resetConfig"
              >重置</el-button
            >
          </div>

          <el-collapse-transition>
            <div class="config-content" v-show="showConfig">
              <div class="config-row">
                <div class="config-item">
                  <span class="label">生成天数：</span>
                  <el-input-number
                    v-model="manualConfig.days"
                    :min="1"
                    :max="150"
                    size="small"
                    controls-position="right"
                    @change="generateManualPreview"
                  />
                  <span class="hint">（最多150天）</span>
                </div>
                <div class="config-item">
                  <span class="label">当前羊只：</span>
                  <el-input-number
                    v-model="manualConfig.currentSheepCount"
                    :min="1"
                    :max="10000"
                    size="small"
                    controls-position="right"
                    @change="updateManualSheepCount"
                  />
                </div>
                <div class="config-item">
                  <span class="label">累计死亡：</span>
                  <el-input-number
                    v-model="manualConfig.deathCount"
                    :min="0"
                    :max="10000"
                    size="small"
                    controls-position="right"
                    disabled
                  />
                  <span class="hint">（只读，来自养殖户数据）</span>
                </div>
                <div class="config-item">
                  <span class="label">开始日期：</span>
                  <el-date-picker
                    v-model="manualConfig.startDate"
                    type="date"
                    placeholder="选择开始日期"
                    format="yyyy/MM/dd"
                    value-format="yyyy-MM-dd"
                    :picker-options="manualDatePickerOptions"
                    size="small"
                    @change="onManualStartDateChange"
                  />
                </div>
              </div>

              <el-table
                :data="manualConfig.feeds"
                border
                size="small"
                class="feed-config-table"
              >
                <el-table-column prop="name" label="饲料名称" width="160">
                  <template #default="{ row }">
                    <span>{{ row.name }}</span>
                    <span class="feed-info">（{{ row.protein }}%）</span>
                  </template>
                </el-table-column>
                <el-table-column label="前两顿参考" width="120" align="center">
                  <template #default="{ row, $index }">
                    <span class="ref-data">{{
                      getManualPreviousFeedDisplay($index, row.name)
                    }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="初始量(斤)" width="130">
                  <template #default="{ $index }">
                    <el-input
                      v-model="manualConfig.feeds[$index].initialWeightDisplay"
                      size="small"
                      placeholder="0"
                      @focus="onManualFocusClear($index, 'initialWeight')"
                      @blur="
                        onManualBlurRestore(
                          $index,
                          'initialWeight',
                          manualConfig.feeds[$index].protein > 0 ? 0 : 1
                        )
                      "
                      @input="onManualInitialWeightInput($index, $event)"
                    />
                  </template>
                </el-table-column>
                <el-table-column label="增量/减量(斤)" width="130">
                  <template #default="{ $index }">
                    <el-input
                      type="number"
                      v-model="manualConfig.feeds[$index].incrementDisplay"
                      size="small"
                      placeholder="0"
                      @focus="onManualFocusClear($index, 'increment')"
                      @blur="
                        onManualBlurRestore(
                          $index,
                          'increment',
                          manualConfig.feeds[$index].protein > 0 ? 0 : 1
                        )
                      "
                      @input="onManualIncrementInput($index, $event)"
                    />
                  </template>
                </el-table-column>
                <el-table-column label="间隔顿数" width="100">
                  <template #default="{ $index }">
                    <div class="interval-wrapper">
                      <el-button
                        size="small"
                        class="interval-btn"
                        :disabled="
                          manualConfig.feeds[$index].intervalMeals <= 1
                        "
                        @click="decreaseManualInterval($index)"
                        >-</el-button
                      >
                      <span class="interval-value">{{
                        manualConfig.feeds[$index].intervalMeals
                      }}</span>
                      <el-button
                        size="small"
                        class="interval-btn"
                        :disabled="
                          manualConfig.feeds[$index].intervalMeals >= 15
                        "
                        @click="increaseManualInterval($index)"
                        >+</el-button
                      >
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-collapse-transition>
        </div>

        <div class="preview-section">
          <div class="preview-header">
            <span class="title"
              >预览数据（共 {{ manualPreviewData.length }} 天）</span
            >
            <span v-if="manualSelectedRows.length > 0" class="selected-info">
              已选中 {{ manualSelectedRows.length }} 天
              <el-button type="text" size="mini" @click="clearManualSelection"
                >清除选择</el-button
              >
            </span>
          </div>
          <div class="preview-table-wrapper">
            <el-table
              :data="manualPreviewData"
              border
              stripe
              max-height="600"
              ref="manualTable"
              @row-click="handleManualRowClick"
              row-class-name="table-row"
              :row-class-name="getManualRowClassName"
              show-summary
              :summary-method="getManualSummaries"
            >
              <el-table-column
                label="选择"
                width="45"
                fixed="left"
                align="center"
                header-align="center"
              >
                <template #header>
                  <el-checkbox
                    :value="isManualAllSelected"
                    :indeterminate="isManualIndeterminate"
                    @change="toggleManualSelectAll"
                  />
                </template>
                <template #default="{ row, $index }">
                  <el-checkbox
                    :value="isManualRowSelected(row)"
                    :disabled="isManualRowDisabled(row, $index)"
                    @change="
                      (checked) => handleManualRowSelect(row, $index, checked)
                    "
                  />
                </template>
              </el-table-column>

              <el-table-column
                label="序号"
                width="70"
                fixed="left"
                align="left"
              >
                <template #default="{ $index }">
                  {{ $index + 1 }}
                </template>
              </el-table-column>

              <el-table-column
                prop="date"
                label="日期"
                width="100"
                fixed="left"
                align="left"
              >
                <template #default="{ row }">
                  {{ formatDateShort(row.date) }}
                </template>
              </el-table-column>

              <el-table-column
                v-for="feed in manualConfig.feeds"
                :key="feed.name"
                :label="feed.name"
                align="left"
                :class-name="
                  feed.protein === 0
                    ? 'feed-column protein-zero-column'
                    : 'feed-column'
                "
              >
                <template #default="{ row }">
                  <div
                    :class="[
                      'feed-cell',
                      { 'protein-zero': feed.protein === 0 },
                    ]"
                  >
                    <span
                      :class="{ 'protein-positive-value': feed.protein > 0 }"
                    >
                      {{
                        formatWeight(
                          row.feedData[feed.name]?.morning || 0,
                          feed.protein
                        )
                      }}
                    </span>
                    /
                    <span
                      :class="{ 'protein-positive-value': feed.protein > 0 }"
                    >
                      {{
                        formatWeight(
                          row.feedData[feed.name]?.afternoon || 0,
                          feed.protein
                        )
                      }}
                    </span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column
                label="均重(斤/只)"
                width="100"
                fixed="right"
                align="left"
              >
                <template #default="{ row }">
                  {{ row.morningAvgWeight }} / {{ row.afternoonAvgWeight }}
                </template>
              </el-table-column>

              <el-table-column
                label="均蛋白(%)"
                width="100"
                fixed="right"
                align="left"
              >
                <template #default="{ row }">
                  {{ row.morningAvgProtein }} / {{ row.afternoonAvgProtein }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </template>

      <!-- ===== 目标导向模式 ===== -->
      <template v-if="generateMode === 'target'">
        <div class="config-section">
          <div
            class="config-header"
            @click="showTargetConfig = !showTargetConfig"
          >
            <span class="title">
              <i
                :class="
                  showTargetConfig
                    ? 'el-icon-arrow-down'
                    : 'el-icon-arrow-right'
                "
              ></i>
              目标参数配置
            </span>
          </div>

          <el-collapse-transition>
            <div
              class="config-content target-config-content"
              v-show="showTargetConfig"
            >
              <div class="target-config-wrapper">
                <TargetGenConfig
                  :config="targetConfig"
                  @update:config="onTargetConfigUpdate"
                  @configChange="onTargetConfigChange"
                />
              </div>
            </div>
          </el-collapse-transition>
        </div>

        <div class="preview-section" v-if="targetPreviewData.length > 0">
          <div class="preview-header">
            <span class="title"
              >预览数据（共
              {{ targetPreviewData.length }} 天，预估出栏天数）</span
            >
            <span v-if="targetSelectedRows.length > 0" class="selected-info">
              已选中 {{ targetSelectedRows.length }} 天
              <el-button type="text" size="mini" @click="clearTargetSelection"
                >清除选择</el-button
              >
            </span>
          </div>
          <div class="preview-table-wrapper">
            <el-table
              :data="targetPreviewData"
              border
              stripe
              max-height="600"
              ref="targetTable"
              @row-click="handleTargetRowClick"
              row-class-name="table-row"
              :row-class-name="getTargetRowClassName"
              show-summary
              :summary-method="getTargetSummaries"
            >
              <el-table-column
                label="选择"
                width="45"
                fixed="left"
                align="center"
                header-align="center"
              >
                <template #header>
                  <el-checkbox
                    :value="isTargetAllSelected"
                    :indeterminate="isTargetIndeterminate"
                    @change="toggleTargetSelectAll"
                  />
                </template>
                <template #default="{ row, $index }">
                  <el-checkbox
                    :value="isTargetRowSelected(row)"
                    :disabled="isTargetRowDisabled(row, $index)"
                    @change="
                      (checked) => handleTargetRowSelect(row, $index, checked)
                    "
                  />
                </template>
              </el-table-column>

              <el-table-column
                prop="dayIndex"
                label="天数"
                width="70"
                fixed="left"
                align="left"
              >
                <template #default="{ row }"> 第{{ row.dayIndex }}天 </template>
              </el-table-column>

              <el-table-column
                v-for="feed in targetFeedNames"
                :key="feed"
                :label="feed"
                align="left"
                class-name="feed-column"
              >
                <template #default="{ row }">
                  <div class="feed-cell">
                    <span>
                      {{
                        formatTargetValue(
                          getTargetFeedWeightValue(row, feed, "morning")
                        )
                      }}
                    </span>
                    /
                    <span>
                      {{
                        formatTargetValue(
                          getTargetFeedWeightValue(row, feed, "afternoon")
                        )
                      }}
                    </span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column
                label="均重(斤/只)"
                width="120"
                fixed="right"
                align="left"
              >
                <template #default="{ row }">
                  {{ formatTargetWeight(row.morningTotalQuantity) }} /
                  {{ formatTargetWeight(row.afternoonTotalQuantity) }}
                </template>
              </el-table-column>

              <el-table-column
                label="均蛋白(%)"
                width="120"
                fixed="right"
                align="left"
              >
                <template #default="{ row }">
                  {{ formatTargetProtein(row.morningTotalProtein) }} /
                  {{ formatTargetProtein(row.afternoonTotalProtein) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div
          v-if="targetPreviewData.length === 0 && targetGenerated"
          class="empty-preview"
        >
          <el-empty description="暂无数据，请配置参数后点击「生成预览」" />
        </div>
      </template>
    </div>

    <span slot="footer" class="dialog-footer">
      <div class="footer-left">
        <el-tag type="info" size="small">已有配料的天数将自动跳过</el-tag>
      </div>
      <div class="footer-right">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button
          v-if="generateMode === 'target'"
          type="primary"
          plain
          @click="generateTargetPreview"
          :loading="targetGenerating"
        >
          生成预览
        </el-button>
        <el-button
          type="primary"
          @click="showSaveDialog"
          :loading="saving"
          :disabled="
            (generateMode === 'manual' && manualPreviewData.length === 0) ||
            (generateMode === 'target' && targetPreviewData.length === 0)
          "
        >
          确认生成并保存
        </el-button>
      </div>
    </span>

    <el-dialog
      title="保存配料计划"
      :visible.sync="saveDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="save-dialog-content">
        <div class="save-info">
          <div class="save-info-item">
            <span class="save-info-label">已选择数据：</span>
            <span class="save-info-value">
              {{
                currentSelectedRows.length > 0
                  ? `第 ${getSelectedRangeText()} 天，共 ${
                      currentSelectedRows.length
                    } 天`
                  : "未选择任何数据，将保存全部数据"
              }}
            </span>
          </div>
          <div class="save-info-item">
            <span class="save-info-label">保存开始日期：</span>
            <el-date-picker
              v-model="saveStartDate"
              type="date"
              placeholder="选择开始日期"
              format="yyyy/MM/dd"
              value-format="yyyy-MM-dd"
              :picker-options="saveDatePickerOptions"
              size="small"
            />
            <span class="save-info-hint">（默认明天）</span>
          </div>
          <div class="save-info-item" v-if="currentSelectedRows.length > 0">
            <span class="save-info-label">预计保存日期：</span>
            <span class="save-info-value">
              {{ formatDateShort(saveStartDate) }}
              ~
              {{ formatDateShort(getEndDate()) }}
              （共 {{ currentSelectedRows.length }} 天）
            </span>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="saveDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSave" :loading="saving">
          确认保存
        </el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { batchSaveSupplierDailyFeeds } from "@/api/user";
import { generateTargetPlan } from "@/utils/target-algorithm.js";
import TargetGenConfig from "../TargetGenConfig/TargetGenConfig.vue";

export default {
  name: "BatchFeedDialog",
  components: {
    TargetGenConfig,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    feeds: {
      type: Array,
      required: true,
    },
    batchId: {
      type: Number,
      required: true,
    },
    batchStartDate: {
      type: String,
      required: true,
    },
    existingDates: {
      type: Array,
      default: () => [],
    },
    defaultSheepCount: {
      type: Number,
      default: 1,
    },
    defaultDeathCount: {
      type: Number,
      default: 0,
    },
    previousFeeds: {
      type: Array,
      default: () => [],
    },
    batchGender: {
      type: Number,
      default: 0,
    },
    batchAvgWeight: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      dialogVisible: false,
      generateMode: "target",

      showConfig: true,
      manualConfig: {
        days: 30,
        currentSheepCount: 1,
        deathCount: 0,
        startDate: "",
        feeds: [],
      },
      manualPreviewData: [],
      manualPreviousFeedsRef: {},
      manualSelectedRows: [],
      manualLastSelectedIndex: null,
      manualRangeDisabledIndices: new Set(),

      showTargetConfig: true,
      targetConfig: {
        sheepCount: 0,
        targetDay: 0,
        targetWeight: 1.2,
        targetOutWeight: 60,
        lambType: "female",
        startWeight: 0,
        proteinFeeds: [],
        additives: [],
        feedGranularity: "meal",
        startProtein: 18,
        peakProtein: 20.5,
        minProtein: 15.4,
        maxFeedPerMeal: 0,
        proteinAtMaxFeed: 0,
        highProteinStartRatios: {},
        highProteinEndRatios: {},
        calculatedDays: 0,
        calculatedA: 0,
      },
      targetPreviewData: [],
      targetFeedNames: [],
      targetGenerated: false,
      targetGenerating: false,
      targetSelectedRows: [],
      targetLastSelectedIndex: null,
      targetRangeDisabledIndices: new Set(),

      saving: false,
      saveDialogVisible: false,
      saveStartDate: "",
      minStartDate: null,
      maxStartDate: null,

      _maxFeedManuallyEdited: false,
    };
  },
  computed: {
    dialogTitle() {
      return this.generateMode === "manual"
        ? "批量生成配料计划（手动）"
        : "批量生成配料计划（目标导向）";
    },
    manualDatePickerOptions() {
      return {
        disabledDate: (time) => {
          const date = new Date(time);
          date.setHours(0, 0, 0, 0);
          if (this.minStartDate && date < this.minStartDate) return true;
          if (this.maxStartDate && date > this.maxStartDate) return true;
          return false;
        },
      };
    },
    saveDatePickerOptions() {
      const batchStart = new Date(this.batchStartDate);
      return {
        disabledDate: (time) => {
          const date = new Date(time);
          date.setHours(0, 0, 0, 0);
          const minDate = new Date(batchStart);
          minDate.setHours(0, 0, 0, 0);
          const maxDate = new Date(batchStart);
          maxDate.setDate(batchStart.getDate() + 149);
          maxDate.setHours(0, 0, 0, 0);
          return date < minDate || date > maxDate;
        },
      };
    },
    currentPreviewData() {
      return this.generateMode === "manual"
        ? this.manualPreviewData
        : this.targetPreviewData;
    },
    currentSelectedRows() {
      return this.generateMode === "manual"
        ? this.manualSelectedRows
        : this.targetSelectedRows;
    },
    isManualAllSelected() {
      return (
        this.manualPreviewData.length > 0 &&
        this.manualSelectedRows.length === this.manualPreviewData.length
      );
    },
    isManualIndeterminate() {
      return (
        this.manualSelectedRows.length > 0 &&
        this.manualSelectedRows.length < this.manualPreviewData.length
      );
    },
    isTargetAllSelected() {
      return (
        this.targetPreviewData.length > 0 &&
        this.targetSelectedRows.length === this.targetPreviewData.length
      );
    },
    isTargetIndeterminate() {
      return (
        this.targetSelectedRows.length > 0 &&
        this.targetSelectedRows.length < this.targetPreviewData.length
      );
    },
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.initManualConfig();
        this.initTargetConfig();
        this.initStartDate();
        this.calculateManualPreviousFeeds();
        this.generateManualPreview();
        this.clearAllSelections();
      }
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit("update:visible", false);
      }
    },
  },
  methods: {
    isManualRowSelected(row) {
      return this.manualSelectedRows.some((r) => r === row);
    },

    isManualRowDisabled(row, index) {
      return this.manualRangeDisabledIndices.has(index);
    },

    getManualRowClassName({ row, rowIndex }) {
      if (this.manualRangeDisabledIndices.has(rowIndex)) {
        return "disabled-row";
      }
      return "";
    },

    handleManualRowClick(row, column, event) {
      if (column && column.label === "选择") return;
      const index = this.manualPreviewData.indexOf(row);
      const isSelected = this.isManualRowSelected(row);
      if (this.isManualRowDisabled(row, index)) return;
      this.handleManualRowSelect(row, index, !isSelected);
    },

    handleManualRowSelect(row, index, checked) {
      if (checked) {
        if (
          this.manualLastSelectedIndex !== null &&
          this.manualLastSelectedIndex !== index
        ) {
          const start = Math.min(this.manualLastSelectedIndex, index);
          const end = Math.max(this.manualLastSelectedIndex, index);

          this.manualRangeDisabledIndices.clear();

          const selectedSet = new Set(this.manualSelectedRows);
          const newSelected = [];
          for (const row of this.manualSelectedRows) {
            const idx = this.manualPreviewData.indexOf(row);
            if (idx === start || idx === end) {
              newSelected.push(row);
            }
          }
          this.manualSelectedRows = newSelected;

          for (let i = start; i <= end; i++) {
            const targetRow = this.manualPreviewData[i];
            if (!this.isManualRowSelected(targetRow)) {
              this.manualSelectedRows.push(targetRow);
            }
            if (i !== start && i !== end) {
              this.manualRangeDisabledIndices.add(i);
            }
          }

          this.manualLastSelectedIndex = index;
        } else {
          if (!this.isManualRowSelected(row)) {
            this.manualSelectedRows.push(row);
          }
          this.manualLastSelectedIndex = index;
        }
      } else {
        const hasRange = this.manualRangeDisabledIndices.size > 0;

        if (hasRange) {
          const selectedIndices = this.manualSelectedRows
            .map((r) => this.manualPreviewData.indexOf(r))
            .filter((i) => i !== -1);
          const minIdx = Math.min(...selectedIndices);
          const maxIdx = Math.max(...selectedIndices);

          if (index === minIdx || index === maxIdx) {
            this.manualSelectedRows = [];
            this.manualRangeDisabledIndices.clear();
            this.manualLastSelectedIndex = null;
            this.$forceUpdate();
            return;
          }
        }

        this.manualRangeDisabledIndices.clear();
        this.manualSelectedRows = this.manualSelectedRows.filter(
          (r) => r !== row
        );

        if (this.manualLastSelectedIndex === index) {
          const stillSelected = this.manualPreviewData.findIndex((r) =>
            this.isManualRowSelected(r)
          );
          this.manualLastSelectedIndex =
            stillSelected !== -1 ? stillSelected : null;
        }

        this.$forceUpdate();
      }
    },

    toggleManualSelectAll(checked) {
      this.manualRangeDisabledIndices.clear();
      if (checked) {
        this.manualSelectedRows = [...this.manualPreviewData];
        this.manualLastSelectedIndex = this.manualPreviewData.length - 1;
      } else {
        this.manualSelectedRows = [];
        this.manualLastSelectedIndex = null;
      }
      this.$forceUpdate();
    },

    clearManualSelection() {
      this.manualSelectedRows = [];
      this.manualLastSelectedIndex = null;
      this.manualRangeDisabledIndices.clear();
      this.$forceUpdate();
    },

    isTargetRowSelected(row) {
      return this.targetSelectedRows.some((r) => r === row);
    },

    isTargetRowDisabled(row, index) {
      return this.targetRangeDisabledIndices.has(index);
    },

    getTargetRowClassName({ row, rowIndex }) {
      if (this.targetRangeDisabledIndices.has(rowIndex)) {
        return "disabled-row";
      }
      return "";
    },

    handleTargetRowClick(row, column, event) {
      if (column && column.label === "选择") return;
      const index = this.targetPreviewData.indexOf(row);
      const isSelected = this.isTargetRowSelected(row);
      if (this.isTargetRowDisabled(row, index)) return;
      this.handleTargetRowSelect(row, index, !isSelected);
    },

    handleTargetRowSelect(row, index, checked) {
      if (checked) {
        if (
          this.targetLastSelectedIndex !== null &&
          this.targetLastSelectedIndex !== index
        ) {
          const start = Math.min(this.targetLastSelectedIndex, index);
          const end = Math.max(this.targetLastSelectedIndex, index);

          this.targetRangeDisabledIndices.clear();

          const selectedSet = new Set(this.targetSelectedRows);
          const newSelected = [];
          for (const row of this.targetSelectedRows) {
            const idx = this.targetPreviewData.indexOf(row);
            if (idx === start || idx === end) {
              newSelected.push(row);
            }
          }
          this.targetSelectedRows = newSelected;

          for (let i = start; i <= end; i++) {
            const targetRow = this.targetPreviewData[i];
            if (!this.isTargetRowSelected(targetRow)) {
              this.targetSelectedRows.push(targetRow);
            }
            if (i !== start && i !== end) {
              this.targetRangeDisabledIndices.add(i);
            }
          }

          this.targetLastSelectedIndex = index;
        } else {
          if (!this.isTargetRowSelected(row)) {
            this.targetSelectedRows.push(row);
          }
          this.targetLastSelectedIndex = index;
        }
      } else {
        const hasRange = this.targetRangeDisabledIndices.size > 0;

        if (hasRange) {
          const selectedIndices = this.targetSelectedRows
            .map((r) => this.targetPreviewData.indexOf(r))
            .filter((i) => i !== -1);
          const minIdx = Math.min(...selectedIndices);
          const maxIdx = Math.max(...selectedIndices);

          if (index === minIdx || index === maxIdx) {
            this.targetSelectedRows = [];
            this.targetRangeDisabledIndices.clear();
            this.targetLastSelectedIndex = null;
            this.$forceUpdate();
            return;
          }
        }

        this.targetRangeDisabledIndices.clear();
        this.targetSelectedRows = this.targetSelectedRows.filter(
          (r) => r !== row
        );

        if (this.targetLastSelectedIndex === index) {
          const stillSelected = this.targetPreviewData.findIndex((r) =>
            this.isTargetRowSelected(r)
          );
          this.targetLastSelectedIndex =
            stillSelected !== -1 ? stillSelected : null;
        }

        this.$forceUpdate();
      }
    },

    toggleTargetSelectAll(checked) {
      this.targetRangeDisabledIndices.clear();
      if (checked) {
        this.targetSelectedRows = [...this.targetPreviewData];
        this.targetLastSelectedIndex = this.targetPreviewData.length - 1;
      } else {
        this.targetSelectedRows = [];
        this.targetLastSelectedIndex = null;
      }
      this.$forceUpdate();
    },

    clearTargetSelection() {
      this.targetSelectedRows = [];
      this.targetLastSelectedIndex = null;
      this.targetRangeDisabledIndices.clear();
      this.$forceUpdate();
    },

    handleManualSelectionChange(selection) {
      this.manualSelectedRows = selection;
    },

    handleTargetSelectionChange(selection) {
      this.targetSelectedRows = selection;
    },

    clearAllSelections() {
      this.manualSelectedRows = [];
      this.manualLastSelectedIndex = null;
      this.manualRangeDisabledIndices.clear();

      this.targetSelectedRows = [];
      this.targetLastSelectedIndex = null;
      this.targetRangeDisabledIndices.clear();

      this.$forceUpdate();
    },

    getSelectedRangeText() {
      const selected = this.currentSelectedRows;
      if (selected.length === 0) return "";

      const previewData = this.currentPreviewData;
      const first = previewData.indexOf(selected[0]);
      const last = previewData.indexOf(selected[selected.length - 1]);

      const firstLabel =
        this.generateMode === "manual" ? first + 1 : selected[0].dayIndex;
      const lastLabel =
        this.generateMode === "manual"
          ? last + 1
          : selected[selected.length - 1].dayIndex;

      return `${firstLabel} ~ ${lastLabel}`;
    },

    showSaveDialog() {
      const previewData = this.currentPreviewData;
      if (previewData.length === 0) {
        this.$message.warning("没有需要保存的数据");
        return;
      }

      if (this.currentSelectedRows.length === 0) {
        if (this.generateMode === "manual") {
          this.manualSelectedRows = [...previewData];
          this.manualLastSelectedIndex = previewData.length - 1;
          this.manualRangeDisabledIndices.clear();
        } else {
          this.targetSelectedRows = [...previewData];
          this.targetLastSelectedIndex = previewData.length - 1;
          this.targetRangeDisabledIndices.clear();
        }
        this.$forceUpdate();
      }

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.saveStartDate = this.formatDate(tomorrow);

      this.saveDialogVisible = true;
    },

    getEndDate() {
      if (!this.saveStartDate || this.currentSelectedRows.length === 0)
        return "";
      const start = new Date(this.saveStartDate);
      const end = new Date(start);
      end.setDate(start.getDate() + this.currentSelectedRows.length - 1);
      return this.formatDate(end);
    },

    async confirmSave() {
      if (!this.saveStartDate) {
        this.$message.warning("请选择开始日期");
        return;
      }

      const selectedRows = this.currentSelectedRows;
      if (selectedRows.length === 0) {
        this.$message.warning("请选择要保存的数据");
        return;
      }

      this.saving = true;
      this.saveDialogVisible = false;

      try {
        let dataToSave = [];

        if (this.generateMode === "manual") {
          const startDate = new Date(this.saveStartDate);
          dataToSave = selectedRows.map((item, index) => {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + index);
            const feedDate = this.formatDate(currentDate);

            const feedItems = this.manualConfig.feeds.map((feed) => ({
              name: feed.name,
              price: feed.price,
              protein: feed.protein,
              morning_weight: item.feedData[feed.name]?.morning || 0,
              afternoon_weight: item.feedData[feed.name]?.afternoon || 0,
            }));

            return {
              feed_date: feedDate,
              morning_sheep_count: this.manualConfig.currentSheepCount,
              afternoon_sheep_count: this.manualConfig.currentSheepCount,
              feed_items: feedItems,
            };
          });
        } else {
          const startDate = new Date(this.saveStartDate);

          const proteinMap = {};
          for (const feed of this.targetConfig.proteinFeeds) {
            proteinMap[feed.name] = feed.protein || 0;
          }
          for (const add of this.targetConfig.additives) {
            proteinMap[add.name] = 0;
          }

          const priceMap = {};
          for (const feed of this.targetConfig.proteinFeeds) {
            priceMap[feed.name] = feed.price || 0;
          }
          for (const add of this.targetConfig.additives) {
            priceMap[add.name] = add.price || 0;
          }

          dataToSave = selectedRows.map((item, index) => {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + index);
            const feedDate = this.formatDate(currentDate);

            const feedItems = [];

            for (const feedItem of item.feedItems) {
              const name = feedItem.name;
              let morningWeight = feedItem.morningWeight || 0;
              let afternoonWeight = feedItem.afternoonWeight || 0;

              morningWeight = this.formatTargetValueAsNumber(morningWeight);
              afternoonWeight = this.formatTargetValueAsNumber(afternoonWeight);

              if (morningWeight > 0 || afternoonWeight > 0) {
                feedItems.push({
                  name: name,
                  price: priceMap[name] || 0,
                  protein: proteinMap[name] || 0,
                  morning_weight: morningWeight,
                  afternoon_weight: afternoonWeight,
                });
              }
            }

            return {
              feed_date: feedDate,
              morning_sheep_count: this.targetConfig.sheepCount,
              afternoon_sheep_count: this.targetConfig.sheepCount,
              feed_items: feedItems,
            };
          });
        }

        const res = await batchSaveSupplierDailyFeeds({
          batch_id: this.batchId,
          daily_feeds: dataToSave,
        });

        if (res.success) {
          const summary = res.data.summary;
          this.$message.success(`成功生成 ${summary.success} 天配料计划`);
          this.dialogVisible = false;
          this.$emit("success");
        } else {
          this.$message.error(res.message || "保存失败");
        }
      } catch (error) {
        console.error("批量保存失败", error);
        this.$message.error(error.message || "批量保存失败");
      } finally {
        this.saving = false;
        this.clearAllSelections();
      }
    },

    onModeChange(mode) {
      this.clearAllSelections();
    },

    initManualConfig() {
      this.manualConfig = {
        days: 30,
        currentSheepCount: this.defaultSheepCount,
        deathCount: this.defaultDeathCount,
        startDate: "",
        feeds: this.feeds.map((feed) => ({
          name: feed.name,
          protein: feed.protein || feed.protein_ratio || 0,
          price: parseFloat(feed.price || feed.current_price || 0),
          initialWeight: 0,
          initialWeightDisplay: "0",
          increment: 0,
          incrementDisplay: "0",
          intervalMeals: 1,
        })),
      };
    },

    initStartDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const startDate = new Date(this.batchStartDate);
      const maxDate = new Date(startDate);
      maxDate.setDate(startDate.getDate() + 150 - this.manualConfig.days);

      this.minStartDate = tomorrow;
      this.maxStartDate = maxDate;

      let defaultStartDate = null;
      if (this.existingDates && this.existingDates.length > 0) {
        const sortedDates = [...this.existingDates].sort();
        const lastDate = new Date(sortedDates[sortedDates.length - 1]);
        const nextDate = new Date(lastDate);
        nextDate.setDate(lastDate.getDate() + 1);
        nextDate.setHours(0, 0, 0, 0);
        defaultStartDate = nextDate;
      } else {
        defaultStartDate = new Date(this.batchStartDate);
        defaultStartDate.setHours(0, 0, 0, 0);
      }

      if (defaultStartDate < tomorrow) defaultStartDate = tomorrow;
      if (defaultStartDate > maxDate) defaultStartDate = maxDate;

      this.manualConfig.startDate = this.formatDate(defaultStartDate);
    },

    calculateManualPreviousFeeds() {
      this.manualPreviousFeedsRef = {};
      if (!this.previousFeeds || this.previousFeeds.length === 0) {
        for (const feed of this.manualConfig.feeds) {
          this.manualPreviousFeedsRef[feed.name] = "0/0";
        }
        return;
      }

      const startDate = new Date(this.manualConfig.startDate);
      let prevDate = new Date(startDate);
      prevDate.setDate(prevDate.getDate() - 1);

      let foundRecord = null;
      for (let i = 0; i < 3; i++) {
        const dateStr = this.formatDate(prevDate);
        const record = this.previousFeeds.find((r) => r.feed_date === dateStr);
        if (record) {
          foundRecord = record;
          break;
        }
        prevDate.setDate(prevDate.getDate() - 1);
      }

      for (const feed of this.manualConfig.feeds) {
        let morning = 0,
          afternoon = 0;
        if (foundRecord) {
          const feedItem = foundRecord.feed_items?.find(
            (f) => f.name === feed.name
          );
          if (feedItem) {
            morning = feedItem.morningWeight || 0;
            afternoon = feedItem.afternoonWeight || 0;
          }
        }
        this.manualPreviousFeedsRef[feed.name] = `${morning}/${afternoon}`;
      }
    },

    getManualPreviousFeedDisplay(index, feedName) {
      return this.manualPreviousFeedsRef[feedName] || "0/0";
    },

    onManualFocusClear(index, field) {
      const feed = this.manualConfig.feeds[index];
      if (field === "initialWeight") {
        feed.initialWeightDisplay = "";
      } else if (field === "increment") {
        feed.incrementDisplay = "";
      }
    },

    onManualBlurRestore(index, field, precision) {
      const feed = this.manualConfig.feeds[index];
      let value =
        field === "initialWeight" ? feed.initialWeight : feed.increment;
      const displayKey =
        field === "initialWeight" ? "initialWeightDisplay" : "incrementDisplay";

      if (feed[displayKey] === "" || feed[displayKey] === undefined) {
        if (precision === 0) {
          feed[displayKey] = Math.round(value).toString();
        } else {
          feed[displayKey] = value.toFixed(precision);
        }
      }
    },

    onManualInitialWeightInput(index, value) {
      const feed = this.manualConfig.feeds[index];
      let numValue = parseFloat(value);
      if (isNaN(numValue)) numValue = 0;
      if (feed.protein > 0) numValue = Math.round(numValue);
      feed.initialWeight = numValue;
      this.generateManualPreview();
    },

    onManualIncrementInput(index, value) {
      const feed = this.manualConfig.feeds[index];
      let numValue = parseFloat(value);
      if (isNaN(numValue)) numValue = 0;
      if (feed.protein > 0) numValue = Math.round(numValue);
      feed.increment = numValue;
      this.generateManualPreview();
    },

    decreaseManualInterval(index) {
      const newValue = this.manualConfig.feeds[index].intervalMeals - 1;
      if (newValue >= 1) {
        this.manualConfig.feeds[index].intervalMeals = newValue;
        this.generateManualPreview();
      }
    },

    increaseManualInterval(index) {
      const newValue = this.manualConfig.feeds[index].intervalMeals + 1;
      if (newValue <= 15) {
        this.manualConfig.feeds[index].intervalMeals = newValue;
        this.generateManualPreview();
      }
    },

    onManualStartDateChange() {
      this.calculateManualPreviousFeeds();
      this.generateManualPreview();
    },

    updateManualSheepCount() {
      this.generateManualPreview();
    },

    generateManualPreview() {
      const previewData = [];
      let currentDate = this.getManualNextStartDate();
      const batchStart = new Date(this.batchStartDate);
      const sheepCount = this.manualConfig.currentSheepCount;

      for (let i = 0; i < this.manualConfig.days; i++) {
        const dateStr = this.formatDate(currentDate);
        const feedMap = {};
        const dayIndex = previewData.length + 1;
        const morningMealIndex = (dayIndex - 1) * 2 + 1;
        const afternoonMealIndex = (dayIndex - 1) * 2 + 2;

        this.manualConfig.feeds.forEach((feedConfig) => {
          const morningWeight = this.calculateManualWeightByMeal(
            feedConfig.initialWeight,
            feedConfig.increment,
            feedConfig.intervalMeals,
            morningMealIndex
          );
          const afternoonWeight = this.calculateManualWeightByMeal(
            feedConfig.initialWeight,
            feedConfig.increment,
            feedConfig.intervalMeals,
            afternoonMealIndex
          );

          feedMap[feedConfig.name] = {
            morning: Math.max(0, Math.round(morningWeight * 10) / 10),
            afternoon: Math.max(0, Math.round(afternoonWeight * 10) / 10),
          };
        });

        const morningStats = this.calculateManualMealStats(
          feedMap,
          sheepCount,
          "morning"
        );
        const afternoonStats = this.calculateManualMealStats(
          feedMap,
          sheepCount,
          "afternoon"
        );

        const currentDateTime = new Date(dateStr);
        const dayOffset = Math.floor(
          (currentDateTime - batchStart) / (1000 * 60 * 60 * 24)
        );
        const dayNumber = dayOffset + 1;

        previewData.push({
          date: dateStr,
          dayNumber: dayNumber,
          feedData: feedMap,
          morningAvgWeight: morningStats.avgWeight,
          morningAvgProtein: morningStats.avgProtein,
          afternoonAvgWeight: afternoonStats.avgWeight,
          afternoonAvgProtein: afternoonStats.avgProtein,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      this.manualPreviewData = previewData;
      this.clearAllSelections();
    },

    calculateManualWeightByMeal(
      initialWeight,
      increment,
      intervalMeals,
      mealIndex
    ) {
      const stepCount = Math.floor((mealIndex - 1) / intervalMeals);
      return initialWeight + increment * stepCount;
    },

    calculateManualMealStats(feedItems, sheepCount, meal) {
      let proteinWeight = 0;
      let totalProtein = 0;

      for (const [feedName, feedData] of Object.entries(feedItems)) {
        const feedConfig = this.manualConfig.feeds.find(
          (f) => f.name === feedName
        );
        if (feedConfig && feedConfig.protein > 0) {
          const weight =
            meal === "morning"
              ? feedData.morning || 0
              : feedData.afternoon || 0;
          proteinWeight += weight;
          totalProtein += weight * (feedConfig.protein / 100);
        }
      }

      const avgWeight = sheepCount > 0 ? proteinWeight / sheepCount : 0;
      const avgProtein =
        proteinWeight > 0 ? (totalProtein / proteinWeight) * 100 : 0;

      return {
        avgWeight: avgWeight.toFixed(1),
        avgProtein: avgProtein.toFixed(1),
      };
    },

    getManualNextStartDate() {
      return new Date(this.manualConfig.startDate);
    },

    resetConfig() {
      this.$confirm("确定要重置所有配置吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          this.initManualConfig();
          this.initStartDate();
          this.calculateManualPreviousFeeds();
          this.generateManualPreview();
          this.$message.success("重置成功");
        })
        .catch(() => {});
    },

    getDefaultTargetOutWeight(lambType, startWeight) {
      const weight = Number(startWeight) || 0;
      if (lambType === "male") {
        return weight >= 45 ? 120 : 100;
      }
      return weight >= 35 ? 75 : 60;
    },

    getDefaultMaxFeed(lambType, targetOutWeight) {
      const t = Number(targetOutWeight) || 0;
      if (lambType === "male") {
        return Math.round((0.65 + 0.01 * t) * 100) / 100;
      }
      return Math.round((0.9273 + 0.004545 * t) * 100) / 100;
    },

    initTargetConfig() {
      const lambType = this.batchGender === 1 ? "male" : "female";
      const startWeight = this.batchAvgWeight;
      const targetOutWeight = this.getDefaultTargetOutWeight(
        lambType,
        startWeight
      );

      const growthResult = this.calculateGrowthParams(
        startWeight,
        lambType,
        targetOutWeight
      );

      const proteinFeeds = this.feeds
        .filter((f) => (f.protein_ratio || f.protein || 0) > 0)
        .map((f) => ({
          name: f.name,
          protein: f.protein_ratio || f.protein || 0,
          price: parseFloat(f.current_price || f.price || 0) || 0,
          totalTons: null,
          startDay: 1,
          endDay: null,
          isFullCourse: true,
        }));

      const additiveFeeds = this.feeds
        .filter((f) => (f.protein_ratio || f.protein || 0) === 0)
        .map((f) => ({
          name: f.name,
          protein: 0,
          price: parseFloat(f.current_price || f.price || 0) || 0,
          startAmount: 0,
          endAmount: 0,
          useMode: "full",
        }));

      const defaultMaxFeed = this.getDefaultMaxFeed(lambType, targetOutWeight);
      const defaultProteinAtMaxFeed = lambType === "female" ? 15.5 : 15;
      const defaultFeedPeakDayPercent = lambType === "female" ? 90 : 85;

      this._maxFeedManuallyEdited = false;

      this.targetConfig = {
        sheepCount: this.defaultSheepCount,
        targetDay: 0,
        targetWeight: 1.2,
        targetOutWeight: targetOutWeight,
        lambType: lambType,
        startWeight: startWeight,
        proteinFeeds: proteinFeeds,
        additives: additiveFeeds,
        feedGranularity: "meal",
        startProtein: 18,
        peakProtein: 20.5,
        minProtein: 15.4,
        calculatedDays: growthResult.calculatedDays,
        calculatedA: growthResult.calculatedA,
        calculatedB: growthResult.calculatedB,
        calculatedAcoef: growthResult.calculatedAcoef,
        dailyADG: growthResult.dailyADG,
        dailyWeight: growthResult.dailyWeight,
        maxFeedPerMeal: defaultMaxFeed,
        proteinAtMaxFeed: defaultProteinAtMaxFeed,
        highProteinStartRatios: {},
        highProteinEndRatios: {},
        highProteinFixedStartAmount: 0.2,
        lowProteinFixedStartAmount: 0.3,
        proteinMinDayPercent: 85,
        feedPeakDayPercent: defaultFeedPeakDayPercent,
      };

      this.targetPreviewData = [];
      this.targetFeedNames = [];
      this.targetGenerated = false;
    },

    onTargetConfigUpdate(config) {
      const growthResult = this.calculateGrowthParams(
        config.startWeight,
        config.lambType,
        config.targetOutWeight
      );

      const updatedConfig = {
        ...config,
        calculatedDays: growthResult.calculatedDays,
        calculatedA: growthResult.calculatedA,
        calculatedB: growthResult.calculatedB,
        calculatedAcoef: growthResult.calculatedAcoef,
        dailyADG: growthResult.dailyADG,
        dailyWeight: growthResult.dailyWeight,
      };

      if (!this._maxFeedManuallyEdited) {
        updatedConfig.maxFeedPerMeal = this.getDefaultMaxFeed(
          config.lambType,
          config.targetOutWeight
        );
      }

      this.targetConfig = updatedConfig;
    },

    onTargetConfigChange(config) {
      const growthResult = this.calculateGrowthParams(
        config.startWeight,
        config.lambType,
        config.targetOutWeight
      );

      const updatedConfig = {
        ...config,
        calculatedDays: growthResult.calculatedDays,
        calculatedA: growthResult.calculatedA,
        calculatedB: growthResult.calculatedB,
        calculatedAcoef: growthResult.calculatedAcoef,
        dailyADG: growthResult.dailyADG,
        dailyWeight: growthResult.dailyWeight,
      };

      if (!this._maxFeedManuallyEdited) {
        updatedConfig.maxFeedPerMeal = this.getDefaultMaxFeed(
          config.lambType,
          config.targetOutWeight
        );
      }

      this.targetConfig = updatedConfig;

      if (
        config.maxFeedPerMeal !== undefined &&
        config._maxFeedManuallyEdited !== undefined
      ) {
        this._maxFeedManuallyEdited = config._maxFeedManuallyEdited;
      }
    },

    calculateMealStats(feedItems, sheepCount, meal) {
      let proteinWeight = 0;
      let totalProtein = 0;

      for (const [name, data] of Object.entries(feedItems)) {
        const weight =
          meal === "morning" ? data.morning || 0 : data.afternoon || 0;
        const protein = data.protein || 0;

        if (protein > 0) {
          proteinWeight += weight;
          totalProtein += weight * (protein / 100);
        }
      }

      const avgWeight = sheepCount > 0 ? proteinWeight / sheepCount : 0;
      const avgProtein =
        proteinWeight > 0 ? (totalProtein / proteinWeight) * 100 : 0;

      return {
        avgWeight: avgWeight,
        avgProtein: avgProtein,
      };
    },

    async generateTargetPreview() {
      const config = this.targetConfig;

      if (!config.proteinFeeds || config.proteinFeeds.length === 0) {
        this.$message.warning("请至少添加一种蛋白饲料");
        return;
      }
      if (config.sheepCount <= 0) {
        this.$message.warning("请设置羊只数量");
        return;
      }
      if (config.startWeight <= 0) {
        this.$message.warning("请设置进圈斤数");
        return;
      }
      if (config.targetOutWeight <= 0) {
        this.$message.warning("请设置出栏目标");
        return;
      }

      this.targetGenerating = true;

      try {
        const result = generateTargetPlan({
          sheepCount: config.sheepCount,
          targetDay: 0,
          lambType: config.lambType || "female",
          startWeight: config.startWeight,
          targetOutWeight: config.targetOutWeight,
          proteinFeeds: config.proteinFeeds,
          additives: config.additives,
          batchSheepCount: this.defaultSheepCount,
          startProtein: config.startProtein || 18,
          peakProtein: config.peakProtein || 20.5,
          minProtein: config.minProtein || 15.4,
          maxFeedPerMeal: config.maxFeedPerMeal || 0,
          proteinAtMaxFeed: config.proteinAtMaxFeed || 0,
          highProteinStartRatios: config.highProteinStartRatios || {},
          highProteinEndRatios: config.highProteinEndRatios || {},
          feedGranularity: config.feedGranularity || "meal",
          feedMode: "national",
          highProteinFixedStartAmount:
            config.highProteinFixedStartAmount || 0.2,
          lowProteinFixedStartAmount: config.lowProteinFixedStartAmount || 0.3,
          proteinMinDayPercent: config.proteinMinDayPercent || 85,
          feedPeakDayPercent: config.feedPeakDayPercent || 90,
        });

        if (!result.success) {
          this.$message.error(result.error || "生成失败");
          this.targetGenerating = false;
          return;
        }

        const previewData = result.data;

        const processedData = previewData.map((item) => {
          const feedMap = {};
          for (const feed of item.feedItems) {
            feedMap[feed.name] = {
              morning: feed.morningWeight || 0,
              afternoon: feed.afternoonWeight || 0,
              protein: feed.protein || 0,
            };
          }

          const morningStats = this.calculateMealStats(
            feedMap,
            item.morningSheepCount || config.sheepCount,
            "morning"
          );
          const afternoonStats = this.calculateMealStats(
            feedMap,
            item.afternoonSheepCount || config.sheepCount,
            "afternoon"
          );

          return {
            ...item,
            morningTotalQuantity:
              Math.round(morningStats.avgWeight * 1000) / 1000,
            afternoonTotalQuantity:
              Math.round(afternoonStats.avgWeight * 1000) / 1000,
            morningTotalProtein: Math.round(morningStats.avgProtein * 10) / 10,
            afternoonTotalProtein:
              Math.round(afternoonStats.avgProtein * 10) / 10,
          };
        });

        this.targetPreviewData = processedData;

        const feedNames = new Set();
        for (const day of processedData) {
          for (const feed of day.feedItems) {
            feedNames.add(feed.name);
          }
        }
        this.targetFeedNames = Array.from(feedNames);
        this.targetGenerated = true;

        this.clearAllSelections();

        this.$message.success(`生成成功，共 ${processedData.length} 天数据`);
      } catch (err) {
        console.error("生成失败:", err);
        this.$message.error(err.message || "生成失败");
      } finally {
        this.targetGenerating = false;
      }
    },

    formatTargetWeight(value) {
      if (value === undefined || value === null || value === 0) return "0.00";
      const num = Number(value);
      if (isNaN(num)) return "0.00";
      return num.toFixed(2);
    },

    formatTargetProtein(value) {
      if (value === undefined || value === null || value === 0) return "0.00";
      const num = Number(value);
      if (isNaN(num)) return "0.00";
      return num.toFixed(2);
    },

    calculateGrowthParams(startWeight, lambType, targetOutWeight) {
      const x = Number(startWeight);
      const isFemale = lambType === "female";
      const targetWeight = targetOutWeight || (isFemale ? 60 : 100);

      if (isNaN(x) || x <= 0) {
        return {
          calculatedDays: 0,
          calculatedA: 0,
          calculatedB: 0,
          calculatedAcoef: 0,
          dailyADG: [],
          dailyWeight: [],
          dailyResults: [],
        };
      }

      let A, b;
      if (isFemale) {
        A = 0.0056 * x + 0.332;
        b = 0.005 * x + 0.05;
      } else {
        A = 0.004 * x + 0.51;
        b = 0.0072 * x + 0.048;
      }

      const T = (targetWeight - x) / A;
      const days = Math.ceil(T);

      const lnT = Math.log(T);
      const denominator = T * lnT - T + 1;
      const a = (A * T - b * (T - 1)) / denominator;

      const dailyADG = [];
      const dailyWeight = [];
      const dailyResults = [];

      for (let t = 1; t <= days; t++) {
        const lnT_t = Math.log(t);
        const adg = a * lnT_t + b;
        const weight = x + a * (t * lnT_t - t + 1) + b * (t - 1);

        dailyADG.push(Math.round(adg * 1000) / 1000);
        dailyWeight.push(Math.round(weight * 100) / 100);
        dailyResults.push({
          day: t,
          adg: Math.round(adg * 1000) / 1000,
          weight: Math.round(weight * 100) / 100,
        });
      }

      return {
        calculatedDays: days,
        calculatedA: Math.round(A * 1000) / 1000,
        calculatedB: Math.round(b * 1000) / 1000,
        calculatedAcoef: Math.round(a * 10000) / 10000,
        dailyADG: dailyADG,
        dailyWeight: dailyWeight,
        dailyResults: dailyResults,
      };
    },

    getTargetFeedWeightValue(row, feedName, meal) {
      const item = row.feedItems.find((f) => f.name === feedName);
      if (!item) return 0;
      const weight =
        meal === "morning" ? item.morningWeight : item.afternoonWeight;
      return weight !== undefined && weight !== null ? weight : 0;
    },

    formatTargetValue(value) {
      if (value === undefined || value === null || value === 0) return "0";

      const num = Number(value);
      if (isNaN(num)) return "0";

      if (num < 80) {
        return Math.round(num).toString();
      }

      if (num >= 150) {
        return Math.round(num / 10) * 10;
      }

      const remainder = num % 5;
      if (remainder < 2.5) {
        return Math.floor(num / 5) * 5;
      } else {
        return Math.ceil(num / 5) * 5;
      }
    },

    formatTargetValueAsNumber(value) {
      if (value === undefined || value === null || value === 0) return 0;

      const num = Number(value);
      if (isNaN(num)) return 0;

      if (num < 80) {
        return Math.round(num);
      }

      if (num >= 150) {
        return Math.round(num / 10) * 10;
      }

      const remainder = num % 5;
      if (remainder < 2.5) {
        return Math.floor(num / 5) * 5;
      } else {
        return Math.ceil(num / 5) * 5;
      }
    },

    // ========== 统计结果格式化：<2000 显示斤，>=2000 显示吨（保留3位小数） ==========
    formatSummaryWeight(total) {
      if (total === undefined || total === null || isNaN(total)) return "0";
      if (total < 2000) {
        return Math.round(total).toString() + "斤";
      }
      return (total / 2000).toFixed(3) + "吨";
    },

    // ========== 手动模式统计行 ==========
    getManualSummaries({ columns, data }) {
      const sums = [];
      const hasSelection = this.manualSelectedRows.length > 0;

      let statData = data;
      if (hasSelection) {
        const selectedIndices = this.manualSelectedRows
          .map((r) => this.manualPreviewData.indexOf(r))
          .filter((i) => i !== -1)
          .sort((a, b) => a - b);

        if (selectedIndices.length > 0) {
          const minIdx = selectedIndices[0];
          const maxIdx = selectedIndices[selectedIndices.length - 1];
          statData = this.manualPreviewData.slice(minIdx, maxIdx + 1);
        }
      }

      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = hasSelection
            ? `已选${statData.length}天合计`
            : `全部${statData.length}天合计`;
          return;
        }

        if (
          column.label === "序号" ||
          column.label === "日期" ||
          column.label === "均重(斤/只)" ||
          column.label === "均蛋白(%)"
        ) {
          sums[index] = "—";
          return;
        }

        const feedConfig = this.manualConfig.feeds.find(
          (f) => f.name === column.label
        );
        if (!feedConfig) {
          sums[index] = "—";
          return;
        }

        let total = 0;
        statData.forEach((row) => {
          const feedData = row.feedData[feedConfig.name];
          if (feedData) {
            total += (feedData.morning || 0) + (feedData.afternoon || 0);
          }
        });

        // 蛋白饲料与添加剂统一按 <2000斤 / >=2000吨 格式化
        if (feedConfig.protein > 0) {
          sums[index] = this.formatSummaryWeight(total);
        } else {
          // 添加剂总量一般较小，若小于2000仍保留1位小数更精确
          if (total < 2000) {
            sums[index] = total.toFixed(1);
          } else {
            sums[index] = (total / 2000).toFixed(3) + "吨";
          }
        }
      });

      return sums;
    },

    // ========== 目标导向模式统计行 ==========
    getTargetSummaries({ columns, data }) {
      const sums = [];
      const hasSelection = this.targetSelectedRows.length > 0;

      let statData = data;
      if (hasSelection) {
        const selectedIndices = this.targetSelectedRows
          .map((r) => this.targetPreviewData.indexOf(r))
          .filter((i) => i !== -1)
          .sort((a, b) => a - b);

        if (selectedIndices.length > 0) {
          const minIdx = selectedIndices[0];
          const maxIdx = selectedIndices[selectedIndices.length - 1];
          statData = this.targetPreviewData.slice(minIdx, maxIdx + 1);
        }
      }

      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = hasSelection
            ? `已选${statData.length}天合计`
            : `全部${statData.length}天合计`;
          return;
        }

        if (
          column.label === "天数" ||
          column.label === "均重(斤/只)" ||
          column.label === "均蛋白(%)"
        ) {
          sums[index] = "—";
          return;
        }

        if (!this.targetFeedNames.includes(column.label)) {
          sums[index] = "—";
          return;
        }

        let total = 0;
        statData.forEach((row) => {
          const morningVal = this.getTargetFeedWeightValue(
            row,
            column.label,
            "morning"
          );
          const afternoonVal = this.getTargetFeedWeightValue(
            row,
            column.label,
            "afternoon"
          );
          total += morningVal + afternoonVal;
        });

        sums[index] = this.formatSummaryWeight(total);
      });

      return sums;
    },

    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatDateShort(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}/${month}/${day}`;
    },

    formatWeight(weight, protein) {
      if (!weight && weight !== 0) return "0";
      if (protein > 0) return Math.round(weight).toString();
      return weight.toFixed(1);
    },

    handleClose() {
      this.dialogVisible = false;
      this.clearAllSelections();
    },
  },
};
</script>

<style scoped>
.batch-feed-dialog :deep(.el-dialog) {
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  margin: 4vh auto !important;
}

.batch-feed-dialog :deep(.el-dialog__body) {
  height: calc(92vh - 120px);
  overflow-y: auto;
  padding: 0 20px;
}

.batch-feed-dialog-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.mode-radio-group {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.mode-radio-group .el-radio-group {
  display: flex;
  gap: 0;
}

.mode-radio-group .el-radio-button {
  margin-right: 0;
}

.mode-radio-group .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 4px 0 0 4px;
}

.mode-radio-group .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 4px 4px 0;
}

.config-section {
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #f5f7fa;
  cursor: pointer;
  user-select: none;
}

.config-header .title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

.config-header .title i {
  margin-right: 8px;
}

.config-content {
  padding: 15px;
  border-top: 1px solid #ebeef5;
}

.config-row {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.config-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.config-item .label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

.config-item .hint {
  font-size: 12px;
  color: #909399;
}

.feed-config-table {
  margin-top: 10px;
}

.feed-info {
  font-size: 12px;
  color: #909399;
  margin-left: 5px;
}

.ref-data {
  font-size: 13px;
  color: #409eff;
  font-weight: 500;
}

.interval-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.interval-wrapper .interval-btn {
  padding: 4px 8px;
  min-width: 28px;
  height: 28px;
}

.interval-wrapper .interval-value {
  width: 30px;
  text-align: center;
  font-size: 14px;
}

.target-config-wrapper {
  padding: 0;
}

.target-config-content {
  max-height: 500px;
  overflow-y: auto;
  padding: 15px;
  border-top: 1px solid #ebeef5;
}

.target-config-content::-webkit-scrollbar {
  width: 6px;
}

.target-config-content::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 3px;
}

.target-config-content::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 3px;
}

.preview-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 5px;
  flex-shrink: 0;
}

.preview-header .title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

.preview-header .selected-info {
  font-size: 13px;
  color: #409eff;
}

.preview-table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.empty-preview {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

:deep(.el-table th) {
  text-align: left !important;
  padding: 6px 4px !important;
}

:deep(.el-table td) {
  text-align: left !important;
}

:deep(.el-table td .cell) {
  padding-left: 4px;
  padding-right: 4px;
  white-space: nowrap;
}

:deep(.feed-column) {
  max-width: 100px;
}

:deep(.feed-column .cell) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.feed-cell {
  text-align: left;
  white-space: nowrap;
}

.feed-cell.protein-zero {
  color: #67c23a;
  font-weight: normal;
}

.protein-positive-value {
  font-weight: bold;
  color: #303133;
}

:deep(.disabled-row) {
  background-color: #f5f7fa !important;
}

:deep(.disabled-row:hover) {
  background-color: #f5f7fa !important;
}

:deep(.el-checkbox.is-disabled .el-checkbox__inner) {
  background-color: #e4e7ed;
  border-color: #d1d5db;
  cursor: not-allowed;
}

:deep(.el-checkbox.is-disabled .el-checkbox__inner::after) {
  border-color: #a8abb2;
}

/* ========== 统计行样式 ========== */
:deep(.el-table__footer-wrapper tbody td) {
  background-color: #f5f7fa;
  font-weight: bold;
  color: #303133;
}

:deep(.el-table__footer-wrapper .cell) {
  white-space: nowrap;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-top: 1px solid #ebeef5;
}

.footer-left {
  float: left;
}

.footer-right {
  float: right;
  display: flex;
  gap: 8px;
}

:deep(.el-input-number) {
  width: 120px;
}

:deep(.el-input-number .el-input__inner) {
  text-align: center;
}

:deep(.el-input--small .el-input__inner) {
  height: 28px;
  line-height: 28px;
}

.save-dialog-content {
  padding: 10px 0;
}

.save-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.save-info-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.save-info-label {
  font-size: 14px;
  color: #606266;
  min-width: 110px;
}

.save-info-value {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.save-info-hint {
  font-size: 12px;
  color: #909399;
}
</style>